import { Pencil } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { getRequest } from '../../../services'

const Feedback = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch complaints from API
  useEffect(() => {
    const fetchComplaints = async () => {
      try {
        setLoading(true);
        const response = await getRequest('/api/Complaint/GetAllComplaints');
        setComplaints(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching complaints:', err);
        setError('Failed to fetch complaints. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchComplaints();
  }, []);

  // Transform API data to match component structure
  const transformComplaintData = (complaint) => {
    return {
      id: complaint.id,
      parentName: complaint.parentName || 'N/A', // Handle missing parent name
      feedbackAbout: complaint.type || 'N/A',
      teacherName: complaint.teacherName || 'N/A',
      studentName: complaint.studentName || 'N/A',
      className: complaint.className || 'N/A',
      campusName: complaint.campusName || 'N/A',
      details: complaint.description || 'No description provided',
      feedback: '', // This field is not in the API response
      status: 'Pending', // Default status since it's not in API
      date: complaint.createdAt ? new Date(complaint.createdAt).toLocaleDateString() : 'N/A'
    };
  };

  const handleEdit = (feedback) => {
    setSelectedFeedback(feedback);
    setFeedbackText(feedback.feedback);
    setIsModalOpen(true);
  };

  const handleView = (feedback) => {
    setSelectedFeedback(feedback);
    setIsViewModalOpen(true);
  };

  const handleSubmit = () => {
    console.log('Updated feedback:', feedbackText);
    setIsModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#241763]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#241763]/80"
        >
          Retry
        </button>
      </div>
    );
  }

  // Transform complaints data for display
  const displayData = complaints.map(transformComplaintData);

  return (
    <>
      <div className="overflow-x-auto mt-6">
        <table className="table-auto w-full mt-6">
          <thead className="py-4 border-b font-bold">
            <tr className="bg-gray-100 border-b">
              <th className='p-3'>Parent Name</th>
              <th className='p-3'>Feedback About</th>
              <th className='p-3'>Teacher Name</th>
              <th className='p-3'>Student Name</th>
              <th className='p-3'>Status</th>
              <th className='p-3'>Details</th>
              <th className='p-3'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((feedback, index) => (
              <tr key={feedback.id || index} className="border-b hover:bg-gray-50">
                <td className="p-3">{feedback.parentName}</td>
                <td className="p-3">{feedback.feedbackAbout}</td>
                <td className="p-3">{feedback.teacherName}</td>
                <td className="p-3">{feedback.studentName}</td>
               
                <td className="p-3">
                  <span className={`px-2 py-1 rounded ${
                    feedback.status === 'Complete' 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {feedback.status}
                  </span>
                </td>
                <td className="p-3">
                  <button 
                    onClick={() => handleView(feedback)}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    View Details
                  </button>
                </td>
                <td className="p-3 space-x-2 flex items-center">
                  <button 
                    onClick={() => handleEdit(feedback)}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                  >
                    <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Edit Feedback</h2>
            <textarea
              value={feedbackText}
              rows={6}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="w-full p-2 border rounded mb-4"
              placeholder="Enter feedback"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#241763] text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-[600px] max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Feedback Details</h2>
            
            <div className="mb-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="font-semibold">Parent Name:</h3>
                  <p className="text-gray-700">{selectedFeedback.parentName}</p>
                </div>
                <div>
                  <h3 className="font-semibold">Date:</h3>
                  <p className="text-gray-700">{selectedFeedback.date}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="font-semibold">Student Name:</h3>
                  <p className="text-gray-700">{selectedFeedback.studentName}</p>
                </div>
                <div>
                  <h3 className="font-semibold">Teacher Name:</h3>
                  <p className="text-gray-700">{selectedFeedback.teacherName}</p>
                </div>
              </div>
              {selectedFeedback.className && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <h3 className="font-semibold">Class Name:</h3>
                    <p className="text-gray-700">{selectedFeedback.className}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Campus Name:</h3>
                    <p className="text-gray-700">{selectedFeedback.campusName}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4">
              <h3 className="font-semibold mb-2">Details:</h3>
              <p className="text-gray-700">{selectedFeedback.details}</p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold mb-2">Feedback:</h3>
              <p className="text-gray-700">{selectedFeedback.feedback || 'No feedback provided yet'}</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Feedback
